/**
 * Onboarding Modal
 * Full-screen guided onboarding experience for new businesses
 *
 * Features:
 * - Full-screen takeover for focus
 * - Progress tracking
 * - Step validation
 * - Smooth transitions
 * - Skip option
 * - Celebratory completion
 */

import React from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { OnboardingWizard } from './OnboardingWizard';

export const OnboardingModal: React.FC = () => {
  const { isOnboarding, currentStep, steps, progress, skipOnboarding } =
    useOnboarding();

  if (!isOnboarding) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-cream-50 overflow-y-auto">
      {/* Header with progress */}
      <div className="sticky top-0 z-10 bg-white border-b border-border-light shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-h3 font-bold text-black">Welcome to Buffables!</h2>
              <p className="text-sm text-black-lighter mt-1">
                Let's set up your loyalty program in a few quick steps
              </p>
            </div>
            <button
              onClick={skipOnboarding}
              className="text-sm text-black-lighter hover:text-black transition-colors"
            >
              Skip for now
            </button>
          </div>

          {/* Progress bar */}
          <div className="relative">
            <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-linear-to-r from-orange-400 to-orange-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-black-lighter mt-2 text-center">
              {progress}% Complete
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300
                    ${
                      step.completed
                        ? 'bg-green-500 text-white'
                        : index === currentStep
                        ? 'bg-orange-400 text-white ring-4 ring-orange-100'
                        : 'bg-cream-200 text-black-lighter'
                    }
                  `}
                >
                  {step.completed ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`
                      h-0.5 w-8 mx-1 transition-all duration-300
                      ${step.completed ? 'bg-green-500' : 'bg-cream-200'}
                    `}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <OnboardingWizard />
      </div>

      {/* Footer with tips */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-50 border-t border-blue-200 py-3 px-6">
        <div className="max-w-4xl mx-auto flex items-start gap-3">
          <div className="shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900">💡 Quick Tip</p>
            <p className="text-sm text-blue-800 mt-0.5">
              {currentStep === 0 && "Choose a category that best represents your business to get personalized recommendations."}
              {currentStep === 1 && "Most businesses start with 10 Stars per $1 spent. You can adjust this anytime."}
              {currentStep === 2 && "Sustainability bonuses help attract eco-conscious customers and drive positive impact!"}
              {currentStep === 3 && "Generate a unique QR code for each location so customers can easily check in."}
              {currentStep === 4 && "You can add more team members later from the Settings page."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
